import { useEffect, useMemo, useState } from 'react';
import {
  fetchShopifyOrders,
  markOrderFulfilled,
  shopifyOrderAdminUrl,
  type ShopifyOrder,
} from '@/lib/shopifyAdmin';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { ExternalLink, RefreshCw, Check } from 'lucide-react';

const FIN_LABELS: Record<string, string> = {
  PAID: 'שולם',
  PARTIALLY_PAID: 'שולם חלקית',
  PENDING: 'ממתין',
  REFUNDED: 'הוחזר',
  PARTIALLY_REFUNDED: 'הוחזר חלקית',
  VOIDED: 'בוטל',
  AUTHORIZED: 'אושר',
};
const FUL_LABELS: Record<string, string> = {
  FULFILLED: 'נשלח',
  PARTIAL: 'נשלח חלקית',
  UNFULFILLED: 'לא נשלח',
  RESTOCKED: 'הוחזר למלאי',
};

const FIN_COLOR: Record<string, string> = {
  PAID: 'bg-emerald-100 text-emerald-700',
  PENDING: 'bg-amber-100 text-amber-700',
  REFUNDED: 'bg-neutral-200 text-neutral-700',
};
const FUL_COLOR: Record<string, string> = {
  FULFILLED: 'bg-emerald-100 text-emerald-700',
  UNFULFILLED: 'bg-amber-100 text-amber-700',
  PARTIAL: 'bg-blue-100 text-blue-700',
};

export default function AdminOrders() {
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ful, setFul] = useState<'all' | 'UNFULFILLED' | 'FULFILLED' | 'PARTIAL'>('all');
  const [fin, setFin] = useState<'all' | 'PAID' | 'PENDING' | 'REFUNDED'>('all');
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchShopifyOrders();
      setOrders(data);
    } catch (e) {
      toast.error('שגיאה בטעינת הזמנות', {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (ful !== 'all' && (o.displayFulfillmentStatus ?? 'UNFULFILLED') !== ful) return false;
      if (fin !== 'all' && (o.displayFinancialStatus ?? 'PENDING') !== fin) return false;
      if (!q) return true;
      const hay = [
        o.name,
        o.customer?.displayName,
        o.customer?.email,
        o.email,
        ...o.lineItems.edges.map((e) => e.node.title),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [orders, search, ful, fin]);

  const handleFulfill = async (orderId: string) => {
    setBusy(orderId);
    try {
      await markOrderFulfilled(orderId);
      toast.success('סומן כנשלח');
      await load();
    } catch (e) {
      toast.error('שגיאה בעדכון', {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">הזמנות</h1>
          <p className="text-sm text-muted-foreground">
            הזמנות מהחנות (Shopify). ניתן לסמן כהזמנה שנשלחה ולפתוח בפאנל החנות.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 me-1" /> רענון
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Input
          placeholder="חיפוש מספר הזמנה, לקוח, מוצר…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[220px]"
        />
        <Select value={ful} onValueChange={(v) => setFul(v as typeof ful)}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="מילוי" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל המשלוחים</SelectItem>
            <SelectItem value="UNFULFILLED">לא נשלח</SelectItem>
            <SelectItem value="PARTIAL">נשלח חלקית</SelectItem>
            <SelectItem value="FULFILLED">נשלח</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fin} onValueChange={(v) => setFin(v as typeof fin)}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="תשלום" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל התשלומים</SelectItem>
            <SelectItem value="PAID">שולם</SelectItem>
            <SelectItem value="PENDING">ממתין</SelectItem>
            <SelectItem value="REFUNDED">הוחזר</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} הזמנות</span>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">טוען…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">אין הזמנות.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">הזמנה</TableHead>
                <TableHead className="text-right">לקוח</TableHead>
                <TableHead className="text-right">מוצרים</TableHead>
                <TableHead className="text-right">סכום</TableHead>
                <TableHead className="text-right">תשלום</TableHead>
                <TableHead className="text-right">מילוי</TableHead>
                <TableHead className="text-right">תאריך</TableHead>
                <TableHead className="text-right">פעולות</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o) => {
                const fulStatus = o.displayFulfillmentStatus ?? 'UNFULFILLED';
                const finStatus = o.displayFinancialStatus ?? 'PENDING';
                return (
                  <TableRow key={o.id}>
                    <TableCell className="font-medium">{o.name}</TableCell>
                    <TableCell>
                      <div className="text-sm">{o.customer?.displayName ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">
                        {o.customer?.email ?? o.email ?? '—'}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[260px]">
                      <ul className="text-xs space-y-0.5">
                        {o.lineItems.edges.slice(0, 4).map((e) => (
                          <li key={e.node.id} className="truncate">
                            {e.node.quantity}× {e.node.title}
                            {e.node.variantTitle && e.node.variantTitle !== 'Default Title'
                              ? ` (${e.node.variantTitle})`
                              : ''}
                          </li>
                        ))}
                        {o.lineItems.edges.length > 4 && (
                          <li className="text-muted-foreground">
                            +{o.lineItems.edges.length - 4} נוספים
                          </li>
                        )}
                      </ul>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {parseFloat(o.totalPriceSet.shopMoney.amount).toLocaleString('he-IL', {
                        style: 'currency',
                        currency: o.totalPriceSet.shopMoney.currencyCode,
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={FIN_COLOR[finStatus] ?? ''}>
                        {FIN_LABELS[finStatus] ?? finStatus}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={FUL_COLOR[fulStatus] ?? ''}>
                        {FUL_LABELS[fulStatus] ?? fulStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums whitespace-nowrap">
                      {new Date(o.createdAt).toLocaleDateString('he-IL')}
                      <div className="text-muted-foreground">
                        {new Date(o.createdAt).toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {fulStatus !== 'FULFILLED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy === o.id}
                            onClick={() => handleFulfill(o.id)}
                          >
                            <Check className="h-3.5 w-3.5 me-1" /> סומן כנשלח
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" asChild>
                          <a href={shopifyOrderAdminUrl(o.id)} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
